// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, BookOpen, Briefcase, FolderArchive, Loader2, Send, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  { key: 'congregacao', label: 'Congregação', icon: BookOpen, color: 'text-congregation', bg: 'bg-congregation-soft', border: 'border-congregation-border' },
  { key: 'manutencao', label: 'Manutenção', icon: Wrench, color: 'text-maintenance', bg: 'bg-maintenance-soft', border: 'border-maintenance-border' },
];

function formatSize(bytes) {
  if (!bytes) return '0 KB';
  if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function TravelerVisitDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [trimesters, setTrimesters] = useState([]);
  const [selected, setSelected] = useState({});
  const [attachLimit, setAttachLimit] = useState(15 * 1024 * 1024);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const allowedAreas = AREAS.filter((a) => (user?.permissions || []).includes(a.key));
  const categoryLabel = AREAS.find((a) => a.key === category)?.label || '';

  useEffect(() => {
    if (!open) {
      setCategory(null);
      setTrimesters([]);
      setSelected({});
      return;
    }
    if (allowedAreas.length === 1) setCategory(allowedAreas[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !category) return;
    setLoading(true);
    setTrimesters([]);
    axios.get(`${API}/traveler-visit/preview`, { params: { category } })
      .then((res) => {
        setTrimesters(res.data.trimesters);
        setAttachLimit(res.data.attach_limit);
        const sel = {};
        res.data.trimesters.forEach((t) => { sel[t.folder_id] = !!t.preselected; });
        setSelected(sel);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Erro ao detectar trimestres'))
      .finally(() => setLoading(false));
  }, [open, category]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const totalSize = trimesters
    .filter((t) => selected[t.folder_id])
    .reduce((s, t) => s + t.total_size, 0);
  const totalFiles = trimesters
    .filter((t) => selected[t.folder_id])
    .reduce((s, t) => s + t.file_count, 0);
  const willUseLink = totalSize > attachLimit;

  const handleSend = async () => {
    if (!email.trim()) { toast.error('Informe o e-mail do destinatário'); return; }
    if (selectedIds.length === 0) { toast.error('Selecione ao menos um trimestre'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/traveler-visit/send`, {
        category, email: email.trim(), message, folder_ids: selectedIds,
      });
      const d = res.data;
      toast.success(
        d.mode === 'attachment'
          ? `Enviado para ${d.sent_to} com ${d.files_included} documento(s) em anexo!`
          : `Enviado para ${d.sent_to} com link de download (${d.files_included} documentos)!`
      );
      onOpenChange(false);
      setEmail('');
      setMessage('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar documentos');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !sending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg" data-testid="traveler-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Visita do Viajante
          </DialogTitle>
          <DialogDescription>
            {category
              ? `Compacta os documentos dos 2 últimos trimestres concluídos de ${categoryLabel} e envia por e-mail.`
              : 'Escolha a área cujos documentos serão enviados para a visita do superintendente de circuito.'}
          </DialogDescription>
        </DialogHeader>

        {!category ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {allowedAreas.length === 0 && (
              <p className="text-sm text-slate-500 col-span-2">Você não tem permissão em nenhuma área.</p>
            )}
            {allowedAreas.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.key}
                  onClick={() => setCategory(a.key)}
                  data-testid={`traveler-area-${a.key}`}
                  className={`flex items-center gap-3 rounded-xl border ${a.border} ${a.bg} p-4 text-left hover:shadow-md transition-all`}
                >
                  <Icon className={`w-6 h-6 ${a.color} shrink-0`} />
                  <div>
                    <p className={`text-sm font-semibold ${a.color}`}>{a.label}</p>
                    <p className="text-xs text-slate-500">Contas da {a.label}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : trimesters.length === 0 ? (
          <p className="text-sm text-slate-500 py-4" data-testid="traveler-no-trimesters">
            Nenhuma pasta de trimestre foi encontrada nesta área.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Trimestres detectados</Label>
              {trimesters.map((t) => (
                <label
                  key={t.folder_id}
                  className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50"
                  data-testid={`traveler-trimester-${t.folder_id}`}
                >
                  <Checkbox
                    checked={!!selected[t.folder_id]}
                    onCheckedChange={(c) => setSelected((s) => ({ ...s, [t.folder_id]: !!c }))}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <FolderArchive className="w-4 h-4 text-slate-500 shrink-0" />
                      <span className="truncate">{t.name}</span>
                      {t.is_current && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 shrink-0">
                          Trimestre atual
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{t.path}</p>
                    <p className="text-xs text-slate-400">{t.file_count} documento(s) · {formatSize(t.total_size)}</p>
                  </div>
                </label>
              ))}
              <p className="text-xs text-slate-500" data-testid="traveler-summary">
                Total: {totalFiles} documento(s) · ~{formatSize(totalSize)} —{' '}
                {willUseLink
                  ? 'será enviado um link de download (válido por 7 dias)'
                  : 'será enviado como anexo do e-mail'}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="traveler-email">E-mail do destinatário</Label>
              <Input
                id="traveler-email"
                type="email"
                placeholder="superintendente@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="traveler-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="traveler-message">Mensagem (opcional)</Label>
              <Textarea
                id="traveler-message"
                rows={2}
                placeholder="Ex.: Documentos para a visita de outubro"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="traveler-message-input"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {category && allowedAreas.length > 1 && (
            <Button variant="ghost" onClick={() => setCategory(null)} disabled={sending}
                    className="mr-auto gap-1.5" data-testid="traveler-back-btn">
              <ArrowLeft className="w-4 h-4" />
              Trocar área
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}
                  data-testid="traveler-cancel-btn">
            Cancelar
          </Button>
          {category && (
            <Button onClick={handleSend} disabled={sending || loading || trimesters.length === 0}
                    className="gap-2" data-testid="traveler-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Compactando e enviando…' : 'Enviar por e-mail'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
